import { isEmpty } from "lodash";
import classnames from "classnames";
import useReportLayout from "./useReportLayout";

import { FormattedDate, FormattedMessage, useIntl } from "react-intl";

// Material UI components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { alpha, Divider, Link, List, ListItem, Popover, Skeleton, Theme } from "@mui/material";

// Components
import { ReportNavigation } from "./ReportNavigation";
import { ReportStickyTitle } from "./ReportStickyTitle";
import { ReportSection, ReportContentBlock, ReportContentGraphs } from "./ReportSection";
import CommonContent from "./CommonContent";
import PrivacyBanner from "./PrivacyBanner";
import StatusCard from "../StatusCard";
import { StatusCardHeader } from "../StatusCardHeader";
import CustomIcon from "components/CustomIcon";
import { RadioButton } from "components/RadioButton";

// Others
import { format } from "date-fns";
import icons from "enums/icons";

// Styles
import styles from "./ReportLayout.styles";

const ReportLayout = (): JSX.Element | null => {
    const classes = styles.ReportLayoutStyles();
    const intl = useIntl();

    const {
        ref,
        reportData,
        reportStatus,
        userPrivacy,
        currentAnchorId,
        interactiveChartRef,
        showLegends,
        shouldShowBanner,
        setShouldShowBanner,
        setShowLegends,
        showPrevPacePopover,
        prevPacePopoverAnchorEl,
        selectedPreviousPace,
        setSelectedPreviousPace,
        selectedPreviousPaceRef,
        headerPaceDate,
        previousAssessmentsList,
        paceStatusInfo,
        retakeAvailableOn,
        lastTaken,
        statusBarColor,
        hasOnGoingAssessment,
        lastCompletedPaceId,
        isLoggedUser,
        handleAddRef,
        handleScrollTo,
        goToNextAnchor,
        handleDownloadReport,
        handleShowPreviousPacePopover,
        handlePreviousReportRequest,
        onClosePrevPacePopover,
        handleRetestPace,
        shouldShrinkHeader,
        triggerShrinkHeaderRef,
    } = useReportLayout();

    return (
        <Box className={classes.paceReport} ref={ref}>
            <Box className={classes.reportBox}>
                <Box
                    className={classnames(classes.reportHeader, {
                        [classes.reportHeaderScrolling]: shouldShrinkHeader,
                    })}
                >
                    {isEmpty(reportData) ? (
                        <Skeleton variant="text" width={300} height={48} />
                    ) : (
                        <Typography variant="h5" className={classes.reportTitle}>
                            {isLoggedUser
                                ? intl.formatMessage({ id: "pace.assessment.heading" })
                                : `${reportData?.meta?.user_name} PACE Assessment: `}

                            {headerPaceDate && selectedPreviousPace?.id === selectedPreviousPaceRef.current?.id && (
                                <>
                                    <FormattedDate value={headerPaceDate} month="long" year="numeric" />{" "}
                                    {selectedPreviousPaceRef.current?.id === null ||
                                    selectedPreviousPaceRef.current?.id === lastCompletedPaceId
                                        ? "(Current)"
                                        : null}
                                </>
                            )}
                        </Typography>
                    )}

                    {/* Header Actions */}
                    <Box display="flex" alignItems="center">
                        {paceStatusInfo.isRetakeAvailable && isLoggedUser && (
                            <Button
                                variant="text"
                                onClick={handleRetestPace}
                                className={classes.headerButtons}
                                startIcon={
                                    <CustomIcon
                                        icon={icons.paceIconColouredDark}
                                        sx={{ fontSize: "16px !important" }}
                                    />
                                }
                            >
                                <Typography sx={{ fontSize: 12 }}>
                                    {intl.formatMessage({ id: "pace.retake" })}
                                </Typography>
                            </Button>
                        )}

                        {previousAssessmentsList && previousAssessmentsList.length > 1 && (
                            <Button
                                variant="text"
                                onClick={handleShowPreviousPacePopover}
                                className={classes.headerButtons}
                                startIcon={<CustomIcon icon={icons.paceIconPrevious} sx={{ fontSize: 18 }} />}
                            >
                                <Typography sx={{ fontSize: 12 }}>
                                    {intl.formatMessage({ id: "pace.previousPace" })}
                                </Typography>
                            </Button>
                        )}

                        <Button
                            variant="text"
                            onClick={handleDownloadReport}
                            className={classes.headerButtons}
                            startIcon={<CustomIcon icon={icons.downloadFile} sx={{ fontSize: "16px !important" }} />}
                        >
                            <Typography sx={{ fontSize: 12 }}>
                                {intl.formatMessage({ id: "download.report" })}
                            </Typography>
                        </Button>
                    </Box>
                </Box>

                <Popover
                    open={showPrevPacePopover}
                    onClose={onClosePrevPacePopover}
                    anchorEl={prevPacePopoverAnchorEl}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                        },
                    }}
                >
                    <Box p={2}>
                        <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                            <CustomIcon icon={icons.paceIconPrevious} sx={{ fontSize: 18, mr: 0.8 }} />
                            <Typography variant="h6" fontWeight={600}>
                                View Previous PACE Assessments
                            </Typography>
                        </Box>
                        <List sx={{ p: 0, pl: 1, mb: 4 }}>
                            {previousAssessmentsList &&
                                previousAssessmentsList.map(({ id, completedDate }, index) => {
                                    const isCurrent = index === 0;
                                    const formattedDate = format(new Date(completedDate), "MMMM yyyy");
                                    const label = isCurrent ? `${formattedDate} [Current]` : formattedDate;
                                    const isSelected = selectedPreviousPace?.id === id;
                                    return (
                                        <ListItem
                                            key={id}
                                            disablePadding
                                            sx={{ mb: 0.5 }}
                                            onClick={() => setSelectedPreviousPace({ id, completedDate })}
                                        >
                                            <RadioButton checked={isSelected} sx={{ p: 0.5 }} />
                                            <Typography variant="body1">{label}</Typography>
                                        </ListItem>
                                    );
                                })}
                        </List>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" marginTop="auto">
                            <Button
                                variant="text"
                                size="small"
                                onClick={onClosePrevPacePopover}
                                sx={{ minWidth: "auto" }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handlePreviousReportRequest}
                                disabled={selectedPreviousPace?.id === null}
                                startIcon={<CustomIcon icon={icons.check} sx={{ fontSize: 14, mt: 0.5 }} />}
                                sx={{ minWidth: "auto", height: 48, padding: 3 }}
                            >
                                Confirm
                            </Button>
                        </Box>
                    </Box>
                </Popover>

                {shouldShowBanner && isLoggedUser && (
                    <PrivacyBanner privacyData={userPrivacy} setShouldShowBanner={setShouldShowBanner} />
                )}

                {isLoggedUser && (
                    <Box className={classes.statusCardWrapper}>
                        <StatusCard
                            sx={{
                                width: 282,
                                borderRadius: 4,
                                overflow: "hidden",
                                alignSelf: "center",
                            }}
                            header={
                                <StatusCardHeader
                                    title={paceStatusInfo.statusLegend}
                                    icon={
                                        paceStatusInfo.statusLegend === "Up to Date"
                                            ? icons.circledCheck
                                            : icons.errorTriangle
                                    }
                                    backgroundColor={statusBarColor}
                                />
                            }
                            body={
                                <Box
                                    sx={(theme: Theme) => ({
                                        backgroundColor: theme.palette.gradients.g02.n1,
                                        color: theme.palette.common.white,
                                        padding: theme.spacing(1.5, 2, 2),
                                    })}
                                >
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <CustomIcon icon={icons.paceIconOutlined} sx={{ fontSize: 18, mr: 1 }} />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                lineHeight: "24px",
                                                lettterSpacing: "0.15px",
                                            }}
                                        >
                                            {intl.formatMessage({ id: "retestCard.title" })}
                                        </Typography>
                                    </Box>
                                    <Typography variant="subtitle2" mb={1}>
                                        {intl.formatMessage({ id: "retestCard.lastTaken" })}{" "}
                                        <FormattedDate
                                            value={new Date(lastTaken)}
                                            month="short"
                                            day="2-digit"
                                            year="numeric"
                                        />
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
                                        {intl.formatMessage({ id: "retestCard.numberOfAssessments" })}{" "}
                                        {reportStatus?.count_completed}
                                    </Typography>

                                    {!paceStatusInfo.isRetakeAvailable ? (
                                        <Box display="flex" alignItems="center" justifyContent="center" py={0.8}>
                                            <CustomIcon
                                                icon={icons.paceIconColouredDark}
                                                sx={{ fontSize: 16, mr: 1 }}
                                            />
                                            <Typography variant="body1" fontSize={13} letterSpacing={0.7}>
                                                <FormattedMessage
                                                    id="retestCard.nextRetakeAvailable"
                                                    values={{ nextRetake: retakeAvailableOn }}
                                                />
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box display="flex" alignItems="center" justifyContent="center">
                                            <Button
                                                variant="contained"
                                                size="small"
                                                fullWidth
                                                sx={(theme: Theme) => ({
                                                    height: 32,
                                                    color: theme.palette.primary.dark,
                                                    background: theme.palette.common.white,
                                                    boxShadow: `0px 5px 7px 0px ${alpha(
                                                        theme.palette.common.white,
                                                        0.16
                                                    )}`,
                                                    "&:hover": {
                                                        background: theme.palette.common.white,
                                                    },
                                                })}
                                                onClick={handleRetestPace}
                                            >
                                                <Box display="flex" alignItems="center">
                                                    <CustomIcon
                                                        icon={icons.paceIconColouredDark}
                                                        sx={{ fontSize: 16, mr: 1 }}
                                                    />
                                                    <Typography variant="body1" fontSize={13}>
                                                        {hasOnGoingAssessment
                                                            ? intl.formatMessage({
                                                                  id: "retestCard.button.label.continue",
                                                              })
                                                            : intl.formatMessage({ id: "retestCard.button.label" })}
                                                    </Typography>
                                                </Box>
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            }
                        />
                        <Box sx={{ marginLeft: "16px", width: "70%", flex: 1 }}>
                            {reportStatus && reportStatus?.count_completed === 1 ? (
                                <>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, fontSize: 20 }}>
                                        {intl.formatMessage({ id: "pace.report.statusWrpper.title1" })}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2, fontSize: 14 }}>
                                        <FormattedMessage
                                            id="pace.report.statusWrapper.subtitle1"
                                            values={{ retakeTime: retakeAvailableOn }}
                                        />
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="subtitle1" mb={2}>
                                        {intl.formatMessage({ id: "pace.report.statusWrpper.title2" })}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2, fontSize: 14 }}>
                                        {intl.formatMessage({ id: "pace.report.statusWrapper.subtitle2" })}
                                    </Typography>
                                </>
                            )}
                            <Link
                                component="button"
                                sx={{
                                    fontSize: 12,
                                    marginBottom: 2,
                                    letterSpacing: "1.25px",
                                    fontFamily: "Lato, sans-serif",
                                }}
                                onClick={() => setShowLegends(!showLegends)}
                            >
                                {!showLegends ? "See more..." : "See less..."}
                            </Link>
                            {showLegends && <CommonContent />}
                        </Box>
                    </Box>
                )}

                {/* REPORT SECTION */}
                {!isEmpty(reportData) && (
                    <Box>
                        <Box
                            sx={{
                                position: "sticky",
                                top: shouldShrinkHeader ? 70 : 100,
                                zIndex: 1000,
                                backgroundColor: "common.white",
                                transition: "top 300ms",
                            }}
                        >
                            <Divider orientation="horizontal" />
                            <ReportNavigation
                                navItems={reportData?.tabs}
                                currentAnchorId={currentAnchorId}
                                navItemClickHandler={handleScrollTo}
                                className={classnames(classes.reportMenu, {
                                    [classes.reportMenuScrolling]: shouldShrinkHeader,
                                })}
                            />
                            <Divider orientation="horizontal" sx={{ marginBottom: 2 }} />
                        </Box>

                        {/* Header shrinker mark */}
                        <Box ref={triggerShrinkHeaderRef} />

                        <Box className={classes.innerBox}>
                            {reportData?.tabs?.map((tab: any, tabIndex: number) => (
                                <div
                                    className={classnames([classes.sectionWrapper, "sectionWrapper"])}
                                    key={tab.id}
                                    id={tab.id}
                                    ref={handleAddRef}
                                >
                                    {/* Sticky title */}
                                    <ReportStickyTitle title={tab.sectionTitle} tabIndex={tabIndex} />

                                    {/* Report section */}
                                    {reportData?.sections
                                        .filter((content: any) => content.tabId === tab.id)
                                        .map((section: any) => (
                                            <ReportSection
                                                key={section.id}
                                                currentSection={section}
                                                tabIndex={tabIndex}
                                                blocks={
                                                    <ReportContentBlock
                                                        data={section?.blocks}
                                                        attachAnchorHandler={handleAddRef}
                                                        handleScrollTo={handleScrollTo}
                                                    />
                                                }
                                                graphs={
                                                    <ReportContentGraphs
                                                        graphId={section?.figureId}
                                                        graphsData={reportData.graphs}
                                                        currentAnchorId={currentAnchorId}
                                                        interactiveChartRef={interactiveChartRef}
                                                        handleScrollTo={handleScrollTo}
                                                    />
                                                }
                                            />
                                        ))}
                                </div>
                            ))}
                        </Box>

                        <Box className={classes.nextButtonWrapper}>
                            <Button
                                onClick={goToNextAnchor}
                                className={classes.nextButton}
                                size="medium"
                                endIcon={<CustomIcon type="outlined" icon={icons.chevronRightThin} />}
                                variant="contained"
                            >
                                {intl.formatMessage({ id: "pace.assessment.button.next" })}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ReportLayout;
